import type { LocationSuggestion } from '@ixigo/cab-core';
import fetch from '@ixigo/fetch';
import type { CabSelectionOptions } from '@/components/cab-selection';
import type { SearchFormData } from '@/components/search-form';
import { executeApiCall } from './helper';
import type {
	ApiErrorResponse,
	BaseContactDetail,
	BaseFarePolicyDetail,
	RideDetails,
} from './types';

export interface TripDetails {
	rideDetails: RideDetails;
	cabSelection: CabSelectionOptions;
}

export interface FareDetailSuccessResponse {
	data: {
		tripDetails: TripDetails;
		farePolicies: BaseFarePolicyDetail;
		contactDetails?: BaseContactDetail;
	};
}

/**
 * Server-driven action popup. When the user's non-airport location sits
 * within the backend's threshold of a DB airport, fareDetails returns this
 * instead of cabs. The FE renders it verbatim and, on the primary action,
 * re-requests fareDetails with `actions: [<primary.action.type>]` so the
 * backend proceeds with the re-mapped trip.
 */

/** Action types the backend may send on actionPopup buttons. */
export type ActionPopupActionType = 'LOCATION_CONFIRMATION' | 'SEARCH_AGAIN';

/** How the FE responds when an action is invoked. */
export type ActionPopupBehavior =
	/** Re-request fareDetails echoing this action (e.g. re-mapped location) */
	| 'refetch'
	/** Close the popup and re-open the location picker to edit the trip */
	| 'edit'
	/** Just dismiss the popup */
	| 'dismiss';

/**
 * Maps each known action type to its FE behavior — the actionPopup analog of
 * `FARE_DETAIL_ERROR_MAP`. Handlers look the behavior up here instead of
 * branching on individual action types; unknown/absent types fall back to
 * `dismiss` (see `getActionPopupBehavior`).
 */
export const ACTION_POPUP_ACTION_MAP: Record<
	ActionPopupActionType,
	{ behavior: ActionPopupBehavior }
> = {
	LOCATION_CONFIRMATION: { behavior: 'refetch' },
	SEARCH_AGAIN: { behavior: 'edit' },
};

/** Resolve a (possibly unknown/absent) action type to its behavior. */
export const getActionPopupBehavior = (
	type?: string | null,
): ActionPopupBehavior =>
	(type && ACTION_POPUP_ACTION_MAP[type as ActionPopupActionType]?.behavior) ||
	'dismiss';

/** True if the action should be echoed back on a re-request (narrows the type). */
export const isRefetchActionType = (
	type?: string | null,
): type is ActionPopupActionType => getActionPopupBehavior(type) === 'refetch';

export interface ActionPopupAction {
	text: string;
	/**
	 * Action descriptor. `type` may be one of `ActionPopupActionType`, null,
	 * absent (`{}`), or an unknown string. The FE resolves it to a behavior via
	 * `ACTION_POPUP_ACTION_MAP` (`getActionPopupBehavior`); unknown/absent types
	 * fall back to `dismiss`.
	 */
	action: { type?: string | null };
}

export interface ActionPopupHighlight {
	/** Full name (route-card sub-line) */
	value: string;
	/** Short code (route-card prominent label), e.g. "IGI" */
	label: string;
}

export interface ActionPopup {
	/** e.g. "LOCATION_CONFIRMATION" */
	type: string;
	title: string;
	/** HTML string (bolded place names etc.) */
	message: string;
	/** Route-card endpoints in pickup → drop order */
	highlights: ActionPopupHighlight[];
	actions: {
		primary: ActionPopupAction;
		secondary?: ActionPopupAction;
	};
}

export interface FareDetailActionPopupResponse {
	data: {
		actionPopup: ActionPopup;
	};
}

export type FareDetailErrorType =
	| 'NOT_SERVICEABLE'
	| 'AIRPORT_ONLY'
	| 'NO_CABS'
	| 'GENERIC_ERROR';
export type FareDetailsErrorResponse = ApiErrorResponse<FareDetailErrorType>;

export type FareDetailsResponse =
	| FareDetailSuccessResponse
	| FareDetailActionPopupResponse
	| FareDetailsErrorResponse;

/**
 * fareDetails response once any action popup has been resolved — cabs or an
 * error, never a popup. The review page is reached post-confirmation, so this
 * is the shape its loader works with.
 */
export type ResolvedFareDetailsResponse =
	| FareDetailSuccessResponse
	| FareDetailsErrorResponse;

interface LocationDetail {
	placeId: LocationSuggestion['placeId'];
	latitude: LocationSuggestion['lat'];
	longitude: LocationSuggestion['lng'];
}

export interface FareDetailsRequest {
	pickup: LocationDetail;
	drop: LocationDetail;
	pickupTime: string;
	/**
	 * Action types the user has accepted (from a prior `actionPopup`), echoed
	 * back so the backend proceeds with the corresponding flow (e.g. re-mapped
	 * trip after a near-airport confirmation).
	 */
	actions?: string[];
}

export const buildFareDetailsRequest = (
	searchFormData: Required<SearchFormData>,
	actions?: string[],
): FareDetailsRequest => {
	// Convert ISO string to local time without timezone info
	const date = new Date(searchFormData.dateTime);
	const offset = date.getTimezoneOffset() * 60000; // offset in milliseconds
	const localDate = new Date(date.getTime() - offset);
	const localISOString = localDate.toISOString().slice(0, -1); // Remove 'Z'

	return {
		pickup: {
			latitude: searchFormData.pickup.lat,
			longitude: searchFormData.pickup.lng,
			placeId: searchFormData.pickup.placeId,
		},
		drop: {
			latitude: searchFormData.drop.lat,
			longitude: searchFormData.drop.lng,
			placeId: searchFormData.drop.placeId,
		},
		pickupTime: localISOString,
		...(actions?.length ? { actions } : {}),
	};
};

export const fetchFareDetails = async (
	searchFormData: Required<SearchFormData>,
	opts: { actions?: string[]; source?: string; signal?: AbortSignal } = {},
): Promise<FareDetailsResponse> => {
	const request = buildFareDetailsRequest(searchFormData, opts.actions);
	const { source, signal } = opts;
	return executeApiCall(
		() =>
			fetch
				.post<FareDetailsResponse>('cabs/api/v1/fareDetails', {
					body: JSON.stringify(request),
					headers: {
						'Content-Type': 'application/json',
					},
					signal,
					...(source && { searchParams: { source } }),
				})
				.json(),
		'fetchFareDetails',
	);
};
